import React, { createContext, useState, useMemo, useEffect, useRef } from 'react';
import { App } from '@capacitor/app';

export type Screen =
  | 'home'
  | 'restaurants'
  | 'restaurantDetail'
  | 'orders'
  | 'account'
  | 'adminDashboard'
  | 'adminDishes'
  | 'adminOrders'
  | 'adminAccount'
  | 'adminAccounting'
  | 'adminReservations'
  | 'adminDrivers'
  | 'adminSubscription'
  | 'adminPayments'
  | 'livreurHome'
  | 'livreurDashboard'
  | 'livreurOrders'
  | 'livreurAccounting'
  | 'livreurAccount'
  | 'livreurActiveOrder'
  | 'forgotPassword'
  | 'updatePassword'
  | 'superAdmin';

export interface View {
  screen: Screen;
  params?: { [key: string]: any };
}

export interface AppContextType {
  view: View;
  setView: (view: View, pushState?: boolean) => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  navigateAndClearCart: (view: View) => void;
}

export const AppContext = createContext<AppContextType>({
  view: { screen: 'home' },
  setView: () => console.warn('AppContext provider not found'),
  isCartOpen: false,
  setIsCartOpen: () => console.warn('AppContext provider not found'),
  navigateAndClearCart: () => console.warn('AppContext provider not found'),
});


const SAVED_VIEW_KEY = 'yumii_last_view';

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [view, setViewInternal] = useState<View>(() => {
    try {
      const saved = localStorage.getItem(SAVED_VIEW_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Basic validation - check if it's a valid object with a screen
        if (parsed && typeof parsed.screen === 'string') {
          // PRO-TIP: We only restore the view for Admins/Livreurs to maintain their dashboard state.
          // Clients are forced back to home on fresh load to avoid being stuck in an old order view.
          if (parsed.screen.startsWith('admin') || parsed.screen.startsWith('livreur')) {
            console.log('[Persistence] Restoring view from localStorage:', parsed);
            return parsed;
          }
        }
      }
    } catch (e) {
      console.error('[Persistence] Error restoring view:', e);
    }
    return { screen: 'home' };
  });
  const [isCartOpen, setIsCartOpenInternal] = useState(false);
  const isHandlingPopState = useRef(false);
  const lastPopTime = useRef(0);
  const viewRef = useRef<View>(view);
  const isCartOpenRef = useRef(isCartOpen);

  // Keep refs in sync
  useEffect(() => {
    viewRef.current = view;
    window.scrollTo(0, 0);
  }, [view]);
  useEffect(() => { isCartOpenRef.current = isCartOpen; }, [isCartOpen]);

  // Sync state with History API & Capacitor Hardware Back Button
  useEffect(() => {
    // 1. Web / Chrome Back Button
    const handlePopState = (event: PopStateEvent) => {
      const now = Date.now();
      // Debounce rapid clicks (less than 300ms)
      if (now - lastPopTime.current < 300) {
        console.log('[History] Rapid click detected, ignoring to prevent app exit');
        return;
      }
      lastPopTime.current = now;

      if (event.state) {
        console.log('[History] PopState detected:', event.state);
        isHandlingPopState.current = true;

        // Restore view screen and params
        if (event.state.screen) {
          setViewInternal({
            screen: event.state.screen,
            params: event.state.params
          });
        }

        // Restore cart state
        setIsCartOpenInternal(!!event.state.isCartOpen);

        setTimeout(() => { isHandlingPopState.current = false; }, 100);
      } else {
        // Fallback to home if no state
        setViewInternal({ screen: 'home' });
        setIsCartOpenInternal(false);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Initial state trap
    if (!window.history.state) {
      window.history.replaceState({ screen: 'home' }, "", "");
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const setView = (newView: View, pushState = true) => {
    console.log('[Navigation] Changing view to:', newView.screen, newView.params);
    setViewInternal(newView);
    
    try {
      // PRO-TIP: We only save navigation state for Pros to maintain their dashboard state.
      // We explicitly REMOVE any saved view if it's a client screen to ensure a reset on next launch.
      if (newView.screen.startsWith('admin') || newView.screen.startsWith('livreur')) {
        localStorage.setItem(SAVED_VIEW_KEY, JSON.stringify(newView));
      } else {
        localStorage.removeItem(SAVED_VIEW_KEY);
      }
    } catch (e) {
      console.error('[Persistence] Error saving view:', e);
    }

    if (pushState && !isHandlingPopState.current) {
      // Comparison to avoid duplicate entries
      const currentState = window.history.state;
      const isSameScreen = currentState?.screen === newView.screen;
      const isSameParams = JSON.stringify(currentState?.params) === JSON.stringify(newView.params);
      const isSameCart = !!currentState?.isCartOpen === isCartOpen;

      if (!isSameScreen || !isSameParams || !isSameCart) {
        window.history.pushState({ ...newView, isCartOpen }, "", "");
      }
    }
  };

  const setIsCartOpen = (isOpen: boolean) => {
    if (isCartOpen === isOpen) return;
    setIsCartOpenInternal(isOpen);

    if (!isHandlingPopState.current) {
      if (isOpen) {
        // Push current view + cart open
        window.history.pushState({ ...view, isCartOpen: true }, "", "");
      } else if (window.history.state?.isCartOpen) {
        // Manual close: only go back if the history suggests we were in a cart state
        window.history.back();
      }
    }
  };

  const navigateAndClearCart = (newView: View) => {
    console.log('[Navigation] Navigate & Clear Cart ->', newView.screen);
    setIsCartOpenInternal(false);
    setViewInternal(newView);
    // Overwrite the current history state directly, avoiding back() popstate issues
    window.history.replaceState({ ...newView, isCartOpen: false }, "", "");
  };

  const contextValue = useMemo(() => ({
    view,
    setView,
    isCartOpen,
    setIsCartOpen,
    navigateAndClearCart
  }), [view, isCartOpen]);

  return React.createElement(AppContext.Provider, { value: contextValue }, children);
}
